import { api } from '@/api'
import { DOMAIN_ACTION } from '@/constants'
import { ErrorCode } from '@/errors/errorCode'
import { IValueChange } from '@/pages/supplier/listing/definition/accountDetails'
import { fetchUserFrontend } from '@/utils/auth/fetchUser'
import { axiosPrivateHelper } from '@/utils/axiosHelper'
import { getSnapshot, types } from 'mobx-state-tree'
import { EventModel } from './eventModel'
import { FaqModel } from './faqModel'
import { GalleryModel } from './galleryModel'
import { ListingDetailModel } from './listingDetailsModel'
import { PromotionsModel } from './promotionsModel'
import { SocialProfileModel } from './socialProfileModel'
import { TestimonialsModel } from './testimonialsModel'
const axios = axiosPrivateHelper()

type SupplierType = {
  id: number
}

const FeatureModel = types.model('FeatureModel').props({
  category: types.maybeNull(types.string),
  category_label: types.maybeNull(types.string),
  created_at: types.maybeNull(types.string),
  created_by: types.maybeNull(types.number),
  id: types.maybeNull(types.number),
  name: types.maybeNull(types.string),
  section: types.maybeNull(types.string),
  updated_at: types.maybeNull(types.string),
})

export const supplierModel = types
  .model('SupplierModel')
  .props({
    id: types.number,
    gallery: types.optional(GalleryModel, {} as any),
    listingDetail: types.optional(ListingDetailModel, {}),
    socialProfile: types.optional(SocialProfileModel, {}),
    testimonials: types.optional(TestimonialsModel, {}),
    promotions: types.optional(PromotionsModel, {}),
    feature_services: types.maybeNull(
      types.optional(types.array(FeatureModel), [])
    ),
    faq: types.optional(FaqModel, {}),
    events: types.optional(EventModel, {}),
    isReadySubmit: types.optional(types.boolean, false),
  })
  .actions((self: any) => {
    const setProfile = (value: SupplierType) => {
      self.id = value.id
    }

    const updateFeatureService = async (id: string, data: any = null) => {
      const payload = { feature_services: getSnapshot(self.feature_services) }
      const URI = `${DOMAIN_ACTION.API.UPDATE_SUPPLIER_LISTING(String(id))}`
      const res = await axios.put(URI, data || payload)
      return res
    }

    const mapValueFeatureServiceChanges = ({ add, value }: IValueChange) => {
      switch (add) {
        case 'feature_services':
          self.feature_services = value
          break
        default:
          null
          break
      }
    }

    const updateSupplierListing = async (payload: any, id: number) => {
      const URI = `${DOMAIN_ACTION.API.UPDATE_SUPPLIER_LISTING(String(id))}`
      const res = await axios.put(URI, payload)
      return res.data
    }

    const uploadFeaturedImage = async (files: File[], supplierId?: string) => {
      if (!files) return { urls: [], images: [] }

      const fileArray = files.map((file) => ({ file }))

      const [err, uploadedImages] = await api.be.uploads.uploadImagesToS3({
        files: fileArray,
        dataType: 'supplier',
        targetId: supplierId,
      })

      return {
        urls: uploadedImages?.map((item) => item.url) || [],
        images: uploadedImages || [],
      }
    }

    const uploadLogoPhoto = async (file: File) => {
      if (!file) return { url: '' }

      const [err, uploadedImages] = await api.be.uploads.uploadImagesToS3({
        files: [{ file }],
        dataType: 'logo',
      })

      if (err) {
        console.log(ErrorCode.e00019, err)
        return { url: '' }
      }

      return { url: uploadedImages[0].url }
    }

    const updateSupplier = async (id: string, payload: any) => {
      const res = await axios.put(
        `${DOMAIN_ACTION.API.PUT_SUPPLIER(id)}`,
        payload
      )

      return res
    }

    const externalUpdate = async (id: string) => {
      const galleryPayload = getSnapshot(self.gallery)
      const res = await updateSupplier(id, galleryPayload)
      self.gallery.setStatus('keep')
      return res.data
    }

    const getTagByCategoryAndSection = async (
      business_category: string,
      section: string
    ) => {
      const URL = `${DOMAIN_ACTION.API.GET_TAG_BY_CATEGORY_AND_SECTION(
        business_category,
        section
      )}`
      const res = await axios.get(URL)
      return res.data
    }

    const getTagByCategory = async (business_category: string) => {
      const URL = `${DOMAIN_ACTION.API.GET_TAG_BY_CATEGORY(business_category)}`
      const res = await axios.get(URL)
      return res.data
    }

    const getLocations = async () => {
      const URL = `${DOMAIN_ACTION.API.GET_LOCATION_LIST}`
      const res = await axios.get(URL)
      return res.data
    }

    const getLocalSupplier = (userData: any, supplierId: number) => {
      return userData.suppliers.find(
        (item: any) => item.id === parseInt(String(supplierId))
      )
    }

    const validListDetail = (userData: any, supplierId: number) => {
      const suppliers = getLocalSupplier(userData, supplierId)
      const { listing_name, business_description, listing_summary } =
        suppliers || {
          listing_name: '',
          business_description: '',
          listing_summary: '',
        }

      const validData = {
        listing_name: listing_name || '',
        business_description: business_description || '',
        listing_summary: listing_summary || '',
      }

      return Object.entries(validData).every(
        (item: any) => item[1] && item[1].length > 0
      )
    }

    const validGallery = (userData: any, supplierId: number) => {
      const suppliers = getLocalSupplier(userData, supplierId)
      const { feature_images, gallery_list } = suppliers || {
        feature_images: null,
        gallery_list: [],
      }
      return (
        feature_images &&
        feature_images.url?.length > 0 &&
        gallery_list.length > 0
      )
    }

    const validSocialLinks = (userData: any, supplierId: number) => {
      const {
        facebook_link,
        instagram_link,
        twitter_link,
        pinterest_link,
        youtube_link,
        vimeo_link,
        tiktok_link,
      } = getLocalSupplier(userData, supplierId)

      const validData = {
        facebook_link: facebook_link || '',
        instagram_link: instagram_link || '',
        twitter_link: twitter_link || '',
        pinterest_link: pinterest_link || '',
        youtube_link: youtube_link || '',
        vimeo_link: vimeo_link || '',
        tiktok_link: tiktok_link || '',
      }

      return Object.entries(validData).some(
        (item: any) => item[1] && item[1].length > 0
      )
    }

    const validTarget = (userData: any, supplierId: number, target: string) => {
      const suppliers = getLocalSupplier(userData, supplierId)
      return suppliers[target] && suppliers[target].length > 0
    }

    const validateCompleteForm = async (supplierId: number) => {
      const res = await fetchUserFrontend() // @todo add error handling
      const userData = await res.json()

      return {
        'listing-details': validListDetail(userData, supplierId),
        'features-and-services': validTarget(
          userData,
          supplierId,
          'feature_services'
        ),
        gallery: validGallery(userData, supplierId),
        'social-profiles': validSocialLinks(userData, supplierId),
        testimonials: validTarget(userData, supplierId, 'testimonials'),
        'offers-and-promotions': validTarget(
          userData,
          supplierId,
          'promotions'
        ),
        faqs: validTarget(userData, supplierId, 'supplier_faq'),
        events: validTarget(userData, supplierId, 'events'),
      }
    }

    const submitForApproval = async (id: number) => {
      const URL = `${DOMAIN_ACTION.API.SUBMIT_FOR_APPROVAL(id)}`
      const response = await axios.put(URL)
      return response
    }

    const validationRequiredForm = () => {
      const {
        listing_name,
        listing_summary,
        business_description,
        business_category,
      }: any = getSnapshot(self.listingDetail)
      const { gallery_list, feature_images }: any = getSnapshot(self.gallery)

      const isRequired = {
        listing_name: !!listing_name,
        listing_summary: !!listing_summary,
        business_description: !!business_description,
        business_category: !!business_category,
        gallery_list: gallery_list.length > 1,
        feature_images: !!feature_images.url,
      }

      return isRequired
    }

    return {
      setProfile,
      uploadFeaturedImage,
      updateSupplier,
      uploadLogoPhoto,
      updateSupplierListing,
      updateFeatureService,
      mapValueFeatureServiceChanges,
      externalUpdate,
      getTagByCategory,
      getTagByCategoryAndSection,
      getLocations,
      validateCompleteForm,
      submitForApproval,
      validationRequiredForm,
    }
  })
